import { useCallback, useMemo } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Map as ImmutableMap } from 'immutable';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import * as EngagementTypes from 'customer-data-objects/engagement/EngagementTypes';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { Small } from 'foundations-components/transitional/components/Small';
import PortalIdParser from 'PortalIdParser';
import { NOTE, TASK, SCHEDULE, LOGGED_CALL, LOGGED_EMAIL, LOGGED_MEETING, LOGGED_LINKED_IN_MESSAGE, LOGGED_POSTAL_MAIL, LOGGED_SMS, LOGGED_WHATS_APP } from 'customer-data-objects/interaction/InteractionTypes';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { Text } from 'foundations-components/components/Text';
import { SubstringHighlighter } from '../helpers/SubstringHighlighter';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const PLURAL_ENGAGEMENT_AND_INTERACTION_TYPE_KEYS = ImmutableMap({
  [EngagementTypes.CALL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.call',
  [EngagementTypes.CONVERSATION_SESSION]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.conversation_session',
  default: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.default',
  [EngagementTypes.EMAIL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.email',
  [EngagementTypes.EMAIL_ACTIVITY]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.email_activity',
  [EngagementTypes.FORWARDED_EMAIL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.forwarded_email',
  [EngagementTypes.INCOMING_EMAIL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.incoming_email',
  [LOGGED_CALL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-call',
  [LOGGED_EMAIL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-email',
  [LOGGED_LINKED_IN_MESSAGE]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-linked-in-message',
  [LOGGED_MEETING]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-meeting',
  [LOGGED_POSTAL_MAIL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-postal-mail',
  [LOGGED_SMS]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-sms',
  [LOGGED_WHATS_APP]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-whats-app',
  [SCHEDULE]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.schedule',
  [EngagementTypes.MEETING]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.meeting',
  [EngagementTypes.NOTE]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.note',
  [EngagementTypes.TASK]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.task'
});
const PERMISSIONLESS_ACTIVITIES = [LOGGED_CALL, LOGGED_EMAIL, LOGGED_LINKED_IN_MESSAGE, LOGGED_POSTAL_MAIL, LOGGED_MEETING, LOGGED_SMS, LOGGED_WHATS_APP, NOTE, TASK, EngagementTypes.NOTE, EngagementTypes.TASK];
const DEFAULT_VALUE = PLURAL_ENGAGEMENT_AND_INTERACTION_TYPE_KEYS.get('default');
const UniversalAssociationsSelectOption = ({
  associationOptionRecord,
  canEdit,
  emailRecipientsIds,
  engagementOrInteractionType,
  onToggleSelectAssociation,
  readOnly,
  searchText,
  shouldDisableLastAssociation,
  toObjectTypeId,
  cardinality,
  allOptionsForType,
  OptionWrapperComponent = undefined
}) => {
  const type = PLURAL_ENGAGEMENT_AND_INTERACTION_TYPE_KEYS.get(engagementOrInteractionType, DEFAULT_VALUE);
  const isNeverLogged = Boolean(associationOptionRecord.get('isNeverLogged'));
  const isPermissionlessActivity = PERMISSIONLESS_ACTIVITIES.includes(engagementOrInteractionType);
  const shouldAllowCommunication = isPermissionlessActivity || associationOptionRecord.get('currentUserCanCommunicate');
  const objectId = associationOptionRecord.get('objectId');
  const isEmailRecipient = Boolean(toObjectTypeId === CONTACT_TYPE_ID && emailRecipientsIds && emailRecipientsIds.includes(objectId));
  const isOneToOne = cardinality === 'ONE_TO_ONE';
  const isDisabledDueToOneToOneSync = isOneToOne && allOptionsForType.filter(option => option.get('isSelected')).size > 1;
  const isReadOnly = readOnly || !canEdit || isEmailRecipient || !shouldAllowCommunication || isNeverLogged || associationOptionRecord.get('isSelected') && shouldDisableLastAssociation;
  const title = useMemo(() => {
    if (readOnly) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.readOnly"
      });
    }
    if (!canEdit) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.tooltipEditPermissionError"
      });
    }
    if (!shouldAllowCommunication) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.tooltipCommunicationPermissionErrorV2"
      });
    }

    // true for Contacts that have opted out of Emails, so we never want to allow them to associate to Emails
    if (isNeverLogged) {
      return null;
    }
    if (isEmailRecipient) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.disableEmailOption"
      });
    }
    if (isDisabledDueToOneToOneSync) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.disableOneToOneSync"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "universalAssociationsSelect.lastAssociationToolTip",
      options: {
        engagementOrInteractionType: unescapedText(type)
      }
    });
  }, [canEdit, isDisabledDueToOneToOneSync, isEmailRecipient, isNeverLogged, readOnly, shouldAllowCommunication, type]);
  const handleToggleSelectAssociation = useCallback(() => {
    onToggleSelectAssociation(associationOptionRecord);
  }, [associationOptionRecord, onToggleSelectAssociation]);
  const Option = useMemo(() => {
    const primaryDisplayLabel = associationOptionRecord.get('primaryDisplayLabel');
    const secondaryDisplayLabel = associationOptionRecord.get('secondaryDisplayLabel');
    const tooltip = secondaryDisplayLabel ? `${primaryDisplayLabel} ${secondaryDisplayLabel}` : `${primaryDisplayLabel}`;
    return /*#__PURE__*/_jsxs(Checkbox, {
      alignment: "center",
      checked: associationOptionRecord.get('isSelected'),
      indeterminate: associationOptionRecord.get('isIndeterminate'),
      "data-selenium-test": "association-option-checkbox",
      "data-selenium-id": objectId,
      onChange: handleToggleSelectAssociation,
      readOnly: isReadOnly,
      disabled: isDisabledDueToOneToOneSync || associationOptionRecord.get('isDisabled'),
      marginBlockEnd: "200",
      children: [/*#__PURE__*/_jsx(Flex, {
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "baseline",
        children: /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          children: /*#__PURE__*/_jsxs(TruncateString, {
            tooltip: tooltip,
            children: [/*#__PURE__*/_jsx(SubstringHighlighter, {
              fullText: associationOptionRecord.get('primaryDisplayLabel'),
              textToHighlight: searchText
            }), /*#__PURE__*/_jsx(Small, {
              use: "help",
              marginInlineStart: "200",
              children: /*#__PURE__*/_jsx(SubstringHighlighter, {
                fullText: associationOptionRecord.get('secondaryDisplayLabel') || '',
                textToHighlight: searchText
              })
            })]
          })
        })
      }), !OptionWrapperComponent && isNeverLogged && /*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "universalAssociationsSelect.neverLog_jsx",
          options: {
            href: `/settings/${PortalIdParser.get()}/objects/activities/email-logging`
          },
          elements: {
            Link
          }
        }),
        children: /*#__PURE__*/_jsx(Badge, {
          use: "candy-apple",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAssociationsSelect.neverLogBadge"
          })
        })
      })]
    });
  }, [associationOptionRecord, handleToggleSelectAssociation, isDisabledDueToOneToOneSync, isNeverLogged, isReadOnly, objectId, searchText, OptionWrapperComponent]);
  if (OptionWrapperComponent) {
    return /*#__PURE__*/_jsx(OptionWrapperComponent, {
      associationOptionRecord: associationOptionRecord,
      children: Option
    });
  }
  if (isReadOnly) {
    return /*#__PURE__*/_jsx(Tooltip, {
      placement: "bottom left",
      title: title,
      children: Option
    });
  }
  return Option;
};
export default UniversalAssociationsSelectOption;